import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionPlanApi, SubscriptionPlan } from '../services/api';
import { NavArrowLeft, Check, EditPencil, Xmark, Plus, Trash } from 'iconoir-react';
import { ConfirmModal } from './ConfirmModal';
import '../admin.css';

const SubscriptionPlansConfig: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<Partial<SubscriptionPlan>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [newPlanData, setNewPlanData] = useState<Partial<SubscriptionPlan>>({});
  const [planToDelete, setPlanToDelete] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await subscriptionPlanApi.getAllAdmin();
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Erreur lors du chargement des plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      display_name: plan.display_name,
      description: plan.description,
      has_ads: plan.has_ads,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      max_boards: plan.max_boards,
      max_cards_total: plan.max_cards_total,
      max_columns_per_board: plan.max_columns_per_board,
      max_storage_mb: plan.max_storage_mb,
      price_per_10_extra_boards: plan.price_per_10_extra_boards,
      price_per_10_extra_boards_yearly: plan.price_per_10_extra_boards_yearly,
      price_per_1000_extra_cards: plan.price_per_1000_extra_cards,
      price_per_1000_extra_cards_yearly: plan.price_per_1000_extra_cards_yearly,
      price_per_extra_gb: plan.price_per_extra_gb,
      price_per_extra_gb_yearly: plan.price_per_extra_gb_yearly,
    });
  };

  const handleSave = async () => {
    if (!editingPlan) return;

    try {
      await subscriptionPlanApi.update(editingPlan.id, formData);
      await loadPlans();
      setEditingPlan(null);
      setFormData({});
      alert('Plan mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du plan');
    }
  };

  const handleCancel = () => {
    setEditingPlan(null);
    setFormData({});
  };

  const handleInputChange = (field: keyof SubscriptionPlan, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNewPlanChange = (field: keyof SubscriptionPlan, value: any) => {
    setNewPlanData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreatePlan = async () => {
    try {
      const planToCreate = {
        name: newPlanData.name || '',
        display_name: newPlanData.display_name || '',
        description: newPlanData.description || '',
        price_monthly: Number(newPlanData.price_monthly) || 0,
        price_yearly: Number(newPlanData.price_yearly) || 0,
        has_ads: newPlanData.has_ads ?? false,
        features: newPlanData.features || [],
        max_boards: newPlanData.max_boards || null,
        max_cards_total: newPlanData.max_cards_total || null,
        max_columns_per_board: newPlanData.max_columns_per_board || null,
        max_users: newPlanData.max_users || null,
        max_storage_mb: newPlanData.max_storage_mb || null,
        price_per_10_extra_boards: Number(newPlanData.price_per_10_extra_boards) || 0,
        price_per_10_extra_boards_yearly: Number(newPlanData.price_per_10_extra_boards_yearly) || 0,
        price_per_1000_extra_cards: Number(newPlanData.price_per_1000_extra_cards) || 0,
        price_per_1000_extra_cards_yearly: Number(newPlanData.price_per_1000_extra_cards_yearly) || 0,
        price_per_extra_gb: Number(newPlanData.price_per_extra_gb) || 0,
        price_per_extra_gb_yearly: Number(newPlanData.price_per_extra_gb_yearly) || 0,
        is_active: true,
        is_admin_only: newPlanData.is_admin_only ?? false,
        sort_order: plans.length + 1,
      };

      await subscriptionPlanApi.create(planToCreate as Omit<SubscriptionPlan, 'id'>);
      await loadPlans();
      setIsCreating(false);
      setNewPlanData({});
      alert('Plan créé avec succès');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création du plan');
    }
  };

  const handleDeletePlan = (planId: number, planName: string) => {
    setPlanToDelete({ id: planId, name: planName });
  };

  const handleConfirmDeletePlan = async () => {
    const plan = planToDelete;
    setPlanToDelete(null);
    if (!plan) return;

    try {
      await subscriptionPlanApi.delete(plan.id);
      await loadPlans();
      alert('Plan supprimé avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      const message = error.response?.data?.message || 'Erreur lors de la suppression du plan';
      alert(message);
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="pricing-config">
      <div className="pricing-header">
        <button onClick={() => navigate('/admin/saas')} className="btn-back">
          <NavArrowLeft width={20} height={20} />
          Retour
        </button>
        
        <div className="pricing-header-info">
          <h1>Configuration des plans d'abonnement</h1>
          <p>Gérez les limites et tarifs de chaque plan</p>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
        >
          <Plus width={18} height={18} />
          Nouveau plan
        </button>
      </div>

      <div className="pricing-warning">
        <strong>Attention :</strong> Les modifications affecteront immédiatement l'affichage sur la page d'inscription. 
        Les abonnements en cours conservent leurs conditions jusqu'au prochain renouvellement.
      </div>

      {isCreating && (
        <div className="plan-config-card plan-create-card">
          <div className="plan-config-header">
            <h2>Nouveau plan d'abonnement</h2>
          </div>
          <div className="plan-edit-form">
            <div className="form-section">
              <h3>Informations générales</h3>
              <div className="form-group">
                <label>Nom technique (unique)</label>
                <input
                  type="text"
                  value={newPlanData.name || ''}
                  onChange={(e) => handleNewPlanChange('name', e.target.value)}
                  placeholder="ex: premium_pro"
                />
              </div>
              <div className="form-group">
                <label>Nom d'affichage</label>
                <input
                  type="text"
                  value={newPlanData.display_name || ''}
                  onChange={(e) => handleNewPlanChange('display_name', e.target.value)}
                  placeholder="ex: Premium Pro"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newPlanData.description || ''}
                  onChange={(e) => handleNewPlanChange('description', e.target.value)}
                  placeholder="Description du plan"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPlanData.has_ads ?? false}
                    onChange={(e) => handleNewPlanChange('has_ads', e.target.checked)}
                  />
                  {' '}Plan avec publicités
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3>Tarifs</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Prix mensuel (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPlanData.price_monthly || ''}
                    onChange={(e) => handleNewPlanChange('price_monthly', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Prix annuel (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPlanData.price_yearly || ''}
                    onChange={(e) => handleNewPlanChange('price_yearly', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Tarifs de dépassement</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>+10 tableaux — mensuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_10_extra_boards || ''}
                    onChange={(e) => handleNewPlanChange('price_per_10_extra_boards', parseFloat(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>+10 tableaux — annuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_10_extra_boards_yearly || ''}
                    onChange={(e) => handleNewPlanChange('price_per_10_extra_boards_yearly', parseFloat(e.target.value))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>+1000 cartes — mensuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_1000_extra_cards || ''}
                    onChange={(e) => handleNewPlanChange('price_per_1000_extra_cards', parseFloat(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>+1000 cartes — annuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_1000_extra_cards_yearly || ''}
                    onChange={(e) => handleNewPlanChange('price_per_1000_extra_cards_yearly', parseFloat(e.target.value))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>+1 Go — mensuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_extra_gb || ''}
                    onChange={(e) => handleNewPlanChange('price_per_extra_gb', parseFloat(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>+1 Go — annuel (€)</label>
                  <input type="number" step="0.01" value={newPlanData.price_per_extra_gb_yearly || ''}
                    onChange={(e) => handleNewPlanChange('price_per_extra_gb_yearly', parseFloat(e.target.value))} />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleCreatePlan}>
                <Check width={18} height={18} />
                Créer le plan
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setIsCreating(false);
                setNewPlanData({});
              }}>
                <Xmark width={18} height={18} />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="plans-config-grid">
        {plans.map(plan => (
          <div key={plan.id} className="plan-config-card">
            <div className="plan-config-header">
              <div>
                <h2>
                  {plan.display_name}
                  {plan.is_admin_only && (
                    <span className="badge" style={{ backgroundColor: '#f5a623', color: 'white', marginLeft: '8px' }}>
                      Admin only
                    </span>
                  )}
                </h2>
                {!plan.is_admin_only && (
                  <span className={`plan-badge ${plan.has_ads ? 'free' : 'premium'}`}>
                    {plan.has_ads ? 'Gratuit' : 'Premium'}
                  </span>
                )}
              </div>
              {editingPlan?.id !== plan.id && (
                <div className="plan-config-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEdit(plan)}
                  >
                    <EditPencil width={18} height={18} />
                    Modifier
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeletePlan(plan.id, plan.display_name)}
                  >
                    <Trash width={18} height={18} />
                    Supprimer
                  </button>
                </div>
              )}
            </div>

            {editingPlan?.id === plan.id ? (
              <div className="plan-edit-form">
                <div className="form-section">
                  <h3>Informations générales</h3>
                  <div className="form-group">
                    <label>Nom affiché</label>
                    <input
                      type="text"
                      value={formData.display_name || ''}
                      onChange={(e) => handleInputChange('display_name', e.target.value)}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="form-control"
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_ads ?? false}
                        onChange={(e) => handleInputChange('has_ads', e.target.checked)}
                      />
                      {' '}Plan avec publicités
                    </label>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Tarifs de base</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Prix mensuel (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_monthly || 0}
                        onChange={(e) => handleInputChange('price_monthly', parseFloat(e.target.value))}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prix annuel (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price_yearly || 0}
                        onChange={(e) => handleInputChange('price_yearly', parseFloat(e.target.value))}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Limites incluses</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Tableaux max</label>
                      <input
                        type="number"
                        value={formData.max_boards || ''}
                        onChange={(e) => handleInputChange('max_boards', e.target.value ? parseInt(e.target.value) : null)}
                        className="form-control"
                        placeholder="Illimité"
                      />
                    </div>
                    <div className="form-group">
                      <label>Cartes max (compte)</label>
                      <input
                        type="number"
                        value={formData.max_cards_total || ''}
                        onChange={(e) => handleInputChange('max_cards_total', e.target.value ? parseInt(e.target.value) : null)}
                        className="form-control"
                        placeholder="Illimité"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Colonnes/tableau max</label>
                      <input
                        type="number"
                        value={formData.max_columns_per_board || ''}
                        onChange={(e) => handleInputChange('max_columns_per_board', e.target.value ? parseInt(e.target.value) : null)}
                        className="form-control"
                        placeholder="Illimité"
                      />
                    </div>
                    <div className="form-group">
                      <label>Stockage (Mo)</label>
                      <input
                        type="number"
                        value={formData.max_storage_mb || ''}
                        onChange={(e) => handleInputChange('max_storage_mb', e.target.value ? parseInt(e.target.value) : null)}
                        className="form-control"
                        placeholder="Illimité"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Tarifs de dépassement</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>+10 tableaux — mensuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_10_extra_boards || 0}
                        onChange={(e) => handleInputChange('price_per_10_extra_boards', parseFloat(e.target.value))} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>+10 tableaux — annuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_10_extra_boards_yearly || 0}
                        onChange={(e) => handleInputChange('price_per_10_extra_boards_yearly', parseFloat(e.target.value))} className="form-control" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>+1000 cartes — mensuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_1000_extra_cards || 0}
                        onChange={(e) => handleInputChange('price_per_1000_extra_cards', parseFloat(e.target.value))} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>+1000 cartes — annuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_1000_extra_cards_yearly || 0}
                        onChange={(e) => handleInputChange('price_per_1000_extra_cards_yearly', parseFloat(e.target.value))} className="form-control" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>+1 Go — mensuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_extra_gb || 0}
                        onChange={(e) => handleInputChange('price_per_extra_gb', parseFloat(e.target.value))} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>+1 Go — annuel (€)</label>
                      <input type="number" step="0.01" value={formData.price_per_extra_gb_yearly || 0}
                        onChange={(e) => handleInputChange('price_per_extra_gb_yearly', parseFloat(e.target.value))} className="form-control" />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-success" onClick={handleSave}>
                    <Check width={18} height={18} />
                    Enregistrer
                  </button>
                  <button className="btn btn-secondary" onClick={handleCancel}>
                    <Xmark width={18} height={18} />
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="plan-config-details">
                <div className="detail-section">
                  <h4>Tarifs de base</h4>
                  <p>Mensuel : <strong>{Number(plan.price_monthly).toFixed(2)}€</strong></p>
                  <p>Annuel : <strong>{Number(plan.price_yearly).toFixed(2)}€</strong></p>
                </div>

                <div className="detail-section">
                  <h4>Limites incluses</h4>
                  <p>Tableaux : <strong>{plan.max_boards || 'Illimité'}</strong></p>
                  <p>Cartes (compte) : <strong>{plan.max_cards_total || 'Illimité'}</strong></p>
                  <p>Colonnes/tableau : <strong>{plan.max_columns_per_board || 'Illimité'}</strong></p>
                  <p>Stockage : <strong>{plan.max_storage_mb ? `${plan.max_storage_mb} Mo` : 'Illimité'}</strong></p>
                </div>

                <div className="detail-section">
                  <h4>Tarifs de dépassement</h4>
                  <p>10 tableaux supp. : <strong>{Number(plan.price_per_10_extra_boards).toFixed(2)}€ / mois</strong> — <strong>{Number(plan.price_per_10_extra_boards_yearly).toFixed(2)}€ / an</strong></p>
                  <p>1000 cartes supp. : <strong>{Number(plan.price_per_1000_extra_cards).toFixed(2)}€ / mois</strong> — <strong>{Number(plan.price_per_1000_extra_cards_yearly).toFixed(2)}€ / an</strong></p>
                  <p>Go supp. : <strong>{Number(plan.price_per_extra_gb).toFixed(2)}€ / mois</strong> — <strong>{Number(plan.price_per_extra_gb_yearly).toFixed(2)}€ / an</strong></p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmModal
        isOpen={planToDelete !== null}
        title="Supprimer le plan"
        message={`Êtes-vous sûr de vouloir supprimer le plan "${planToDelete?.name}" ?`}
        confirmText="Supprimer"
        onConfirm={handleConfirmDeletePlan}
        onCancel={() => setPlanToDelete(null)}
        variant="danger"
      />
    </div>
  );
};

export default SubscriptionPlansConfig;
